/*
 * Copyright 2026 Sony Corporation
 */

import { Button, ButtonGroup, Paper } from '@mui/material';
import type { RefObject } from 'react';

import type { SceneEditor } from '../../scene/scene-editor';

// alpha = -PI/2 is the default heading; Side is turned a quarter from it.
const ANGLES = [
  { label: 'Top', alpha: -Math.PI / 2, beta: 0.01 },
  { label: 'Center', alpha: -Math.PI / 2, beta: Math.PI / 2 },
  { label: 'Side', alpha: 0, beta: Math.PI / 2 },
] as const;

export interface ViewAngleButtonsProps {
  editorRef: RefObject<SceneEditor | null>;
}

/** Snaps the viewport camera to a preset angle around the active scene. */
export const ViewAngleButtons = ({ editorRef }: ViewAngleButtonsProps) => {
  const show = (alpha: number, beta: number) => {
    const editor = editorRef.current;
    if (!editor) {
      return;
    }
    const { center, radius } = editor.activeBounds();
    editor.applyView({
      alpha,
      beta,
      radius: radius * 2.2,
      target: { x: center.x, y: center.y, z: center.z },
    });
  };

  return (
    <Paper elevation={3} className="absolute left-3 top-3 z-10">
      <ButtonGroup size="small" variant="text" aria-label="View angle">
        {ANGLES.map((angle) => (
          <Button key={angle.label} onClick={() => show(angle.alpha, angle.beta)}>
            {angle.label}
          </Button>
        ))}
      </ButtonGroup>
    </Paper>
  );
};
